// Ask the office: reading and writing threads (docs/op-checklists-plan.md, session 3), shared by
// the techs app (threadTech.js) and the office's Tech questions page (techQuestions.js). Rules and
// record shapes are in threadCore.js.
//
// A message is saved durably (trackedDb keeps it on a phone with no signal and sends it later).
// The thread's summary is written at once when there's a connection, and by the onWoThreadMessage
// Cloud Function in any case, so a message sent offline still shows up in the office's inbox.

import { ref, onValue, push, remove, update, query, orderByChild, equalTo } from 'firebase/database';
import { database, isFirebaseConnected } from './initFirebase';
import { set as dbSet } from './trackedDb';
import * as T from './threadCore';

export const newMessageId = () => push(ref(database, 'woThreads')).key;
const online = () => navigator.onLine && isFirebaseConnected();

/**
 * Post a message (from threadCore.newMessage/eventMessage). info: { workOrder, boatName, customerName, customerId }.
 * Returns { id, saved } where saved resolves once the server has it.
 */
export function postMessage(wo, msg, { id = newMessageId(), info = {} } = {}) {
  const k = T.woKey(wo);
  const full = { ...msg, Work_Order: String(info.workOrder || wo) };
  if (info.boatName) full.Boat_Name = String(info.boatName);
  if (info.customerName) full.Customer_Name = String(info.customerName);
  if (info.customerId) full.Customer_ID = String(info.customerId);
  const saved = dbSet(ref(database, `woThreads/${k}/Messages/${id}`), full);
  if (online()) {
    const u = T.postUpdates(wo, id, full, info);
    delete u[`woThreads/${k}/Messages/${id}`];
    update(ref(database), u).catch(err => console.warn('Thread: summary not updated (the server will)', err));
  }
  return { id, saved };
}

export function markRead(wo, uid, role) {
  if (!wo || !uid || !online()) return;
  update(ref(database), T.readUpdates(wo, uid, role)).catch(err => console.warn('Thread: read mark not saved', err));
}

/** Answers waiting for this person (written by functions/alerts.js), and clearing one once it's opened. */
export const watchAnswers = (uid, cb) => onValue(ref(database, `woThreadInbox/${uid}`), s => cb(s.val() || {}), err => console.warn('Thread: answers not available', err));
export const clearAnswer = (uid, wo) => { if (uid && wo) remove(ref(database, `woThreadInbox/${uid}/${T.woKey(wo)}`)).catch(err => console.warn('Thread: answer not cleared', err)); };

export const markAnswered = wo => update(ref(database, `woThreadMeta/${T.woKey(wo)}`), { Waiting_On: null });

/** Listen to one thread's messages (only while it's open). Returns the unsubscribe. */
export const watchThread = (wo, cb) => onValue(ref(database, `woThreads/${T.woKey(wo)}/Messages`), s => cb(s.val() || {}), err => console.warn('Thread unavailable', err));
export const watchMeta = (wo, cb) => onValue(ref(database, `woThreadMeta/${T.woKey(wo)}`), s => cb(s.val() || null), () => cb(null));
export const watchMyRead = (uid, wo, cb) => onValue(ref(database, `woThreadRead/${uid}/${T.woKey(wo)}`), s => cb(s.val() || 0), () => cb(0));

/** The office inbox: every thread's summary, and this person's read marks. */
export const watchAllMeta = cb => onValue(ref(database, 'woThreadMeta'), s => cb(s.val() || {}), err => console.warn('Tech questions unavailable', err));
export const watchMyReads = (uid, cb) => onValue(ref(database, `woThreadRead/${uid}`), s => cb(s.val() || {}), () => cb({}));
/** Threads waiting on the office (the rail count). */
export const watchWaiting = cb => onValue(query(ref(database, 'woThreadMeta'), orderByChild('Waiting_On'), equalTo('office')), s => cb(Object.keys(s.val() || {}).length), () => cb(0));
